(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  function setupAutoplayVideos() {
    const videos = qsa('video[data-autoplay-video], video[autoplay]');
    if (!videos.length) return;
    const retries = new WeakMap();
    const retryTimers = new WeakMap();
    const prepareVideo = (video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.playsInline = true;
      video.autoplay = true;
      video.preload = 'auto';
      video.disablePictureInPicture = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('preload', 'auto');
    };
    const queueRetry = (video) => {
      const attempt = retries.get(video) || 0;
      if (attempt >= 3 || document.hidden || video.ended) return;
      window.clearTimeout(retryTimers.get(video));
      retries.set(video, attempt + 1);
      retryTimers.set(video, window.setTimeout(() => playVideo(video), 350 * (attempt + 1)));
    };
    const playVideo = (video) => {
      if (document.hidden || !video.isConnected) return;
      prepareVideo(video);
      const playback = video.play();
      if (playback && typeof playback.then === 'function') {
        playback.then(() => retries.set(video, 0)).catch(() => queueRetry(video));
      }
    };
    const startAllVideos = () => {
      if (document.hidden) return;
      videos.forEach((video) => {
        retries.set(video, 0);
        playVideo(video);
      });
    };
    videos.forEach((video) => {
      prepareVideo(video);
      ['loadedmetadata', 'loadeddata', 'canplay'].forEach((eventName) => {
        video.addEventListener(eventName, () => playVideo(video), { once: true });
      });
      video.addEventListener('pause', () => queueRetry(video));
      if (video.readyState === 0) video.load();
    });
    window.addEventListener('pageshow', startAllVideos);
    window.addEventListener('focus', startAllVideos);
    document.addEventListener('visibilitychange', startAllVideos);
    startAllVideos();
  }

  function setupCheckoutTerms() {
    qsa('[data-checkout-terms-form], .cart-page__form').forEach((form) => {
      const terms = qs('[data-checkout-terms-input]', form);
      const submit = qs('[data-checkout-submit]', form);
      const error = qs('[data-checkout-terms-error]', form);
      if (!terms || !submit || form.dataset.termsReady === 'true') return;
      form.dataset.termsReady = 'true';
      const syncTerms = () => {
        submit.disabled = !terms.checked;
        if (terms.checked && error) error.hidden = true;
      };
      terms.addEventListener('change', syncTerms);
      form.addEventListener('submit', (event) => {
        if (event.submitter?.name !== 'checkout' || terms.checked) return;
        event.preventDefault();
        if (error) error.hidden = false;
        terms.focus();
      });
      syncTerms();
    });
  }

  function setOpen(panel, open) {
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.classList.toggle('drawer-open', open);
    qsa(`[aria-controls="${panel.id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', String(open)));
    if (open) {
      const focusTarget = qs('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])', panel);
      focusTarget?.focus({ preventScroll: true });
    }
  }

  function replaceCartDrawer(html) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer || !html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const updated = qs('[data-cart-drawer]', parsed);
    if (!updated) return false;
    drawer.replaceWith(updated);
    setupCheckoutTerms();
    return true;
  }

  async function refreshCartDrawer() {
    if (!window.themeRoutes?.cart_section_url) return false;
    const separator = window.themeRoutes.cart_section_url.includes('?') ? '&' : '?';
    const response = await fetch(`${window.themeRoutes.cart_section_url}${separator}_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart drawer refresh failed');
    const payload = await response.json();
    return replaceCartDrawer(payload['cart-drawer']);
  }

  function cartAjaxUrl(route) {
    if (!route) return '/cart/add.js';
    return route.endsWith('.js') ? route : `${route}.js`;
  }

  async function updateCartCount() {
    const cartRoute = window.themeRoutes?.cart_url || '/cart';
    const cartResponse = await fetch(cartAjaxUrl(cartRoute), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();
    qsa('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
  }

  document.addEventListener('click', async (event) => {
    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      setOpen(qs('#CartDrawer'), true);
      return;
    }
    if (event.target.closest('[data-cart-close]')) {
      setOpen(qs('#CartDrawer'), false);
      return;
    }
    if (event.target.closest('[data-menu-open]')) {
      setOpen(qs('#MobileMenu'), true);
      return;
    }
    if (event.target.closest('[data-menu-close]')) {
      setOpen(qs('#MobileMenu'), false);
      return;
    }
    const button = event.target.closest('[data-cart-qty], [data-cart-remove]');
    if (button) {
      const item = button.closest('[data-line]');
      if (!item) return;
      const line = Number(item.dataset.line);
      const qtyNode = item.querySelector('.cart-item__controls span');
      const current = Number(qtyNode?.textContent || 1);
      const next = button.hasAttribute('data-cart-remove') ? 0 : Math.max(0, current + Number(button.dataset.cartQty || 0));
      const status = qs('[data-cart-status]');
      button.disabled = true;
      if (status) status.textContent = 'Updating cart';
      try {
        const response = await fetch(window.themeRoutes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: next })
        });
        if (!response.ok) throw new Error('Cart update failed');
        const drawerUpdated = await refreshCartDrawer();
        await updateCartCount();
        if (!drawerUpdated) window.location.assign(window.themeRoutes?.cart_url || '/cart');
      } catch (error) {
        if (status) status.textContent = 'Cart update failed. Please try again.';
        button.disabled = false;
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(qs('#CartDrawer'), false);
      setOpen(qs('#MobileMenu'), false);
    }
  });

  qsa('.quick-add').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button', form);
      const card = form.closest('.product-card');
      const status = qs('.quick-add__status', form);
      const defaultLabel = button.dataset.quickAddLabel || button.textContent.trim() || 'Quick add';
      const variantId = qs('input[name="id"]', form)?.value;
      let productWasAdded = false;
      const markAdded = () => {
        form.classList.remove('is-adding', 'is-error');
        form.classList.add('is-added');
        card?.classList.remove('product-card--adding');
        card?.classList.add('product-card--added');
        button.textContent = 'Added to bag';
        if (status) status.textContent = 'Added to your bag.';
      };
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      form.classList.remove('is-added', 'is-error');
      form.classList.add('is-adding');
      card?.classList.remove('product-card--added');
      card?.classList.add('product-card--adding');
      button.textContent = 'Adding to bag...';
      if (status) status.textContent = 'Adding this item to your bag.';
      try {
        if (!variantId) throw new Error('This product has no purchasable variant.');
        const formData = new FormData(form);
        formData.set('sections', 'cart-drawer');
        formData.set('sections_url', window.location.pathname);
        const addResponse = await fetch(cartAjaxUrl(window.themeRoutes?.cart_add_url || form.action), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!addResponse.ok) {
          const errorPayload = await addResponse.json().catch(() => ({}));
          throw new Error(errorPayload.description || errorPayload.message || 'Add to cart failed');
        }
        const addPayload = await addResponse.json().catch(() => ({}));
        productWasAdded = true;
        await updateCartCount();
        try {
          const updatedFromAdd = replaceCartDrawer(addPayload?.sections?.['cart-drawer']);
          const drawerUpdated = updatedFromAdd || await refreshCartDrawer();
          if (!drawerUpdated) throw new Error('Cart drawer response was incomplete');
        } catch {
          // The product was added, but the section endpoint did not return a drawer update.
          // Use Shopify's full cart page instead of showing a stale empty drawer.
          window.location.assign(window.themeRoutes?.cart_url || '/cart');
          return;
        }
        setOpen(qs('#CartDrawer'), true);
        markAdded();
      } catch (error) {
        if (productWasAdded) {
          setOpen(qs('#CartDrawer'), true);
          markAdded();
          return;
        }
        form.classList.remove('is-adding');
        form.classList.add('is-error');
        card?.classList.remove('product-card--adding');
        button.textContent = 'Unavailable';
        if (status) status.textContent = error.message || 'This item could not be added.';
      } finally {
        setTimeout(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          form.classList.remove('is-adding', 'is-added', 'is-error');
          card?.classList.remove('product-card--adding', 'product-card--added');
          button.textContent = defaultLabel;
        }, 1200);
      }
    });
  });

  const filterForm = qs('#CollectionFilters');
  if (filterForm) {
    filterForm.addEventListener('change', () => filterForm.submit());
  }

  setupCheckoutTerms();
  setupAutoplayVideos();

  const mobileAtc = qs('[data-scroll-product-form]');
  if (mobileAtc) {
    mobileAtc.addEventListener('click', () => qs('.product-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  qsa('.product-form').forEach((form) => {
    const variantScript = qs('[data-product-variants]', form);
    if (!variantScript) return;
    let variants = [];
    try {
      variants = JSON.parse(variantScript.textContent || '[]');
    } catch (error) {
      return;
    }
    const idInput = qs('input[name="id"]', form);
    const submit = qs('button[type="submit"]', form);
    const mobileSubmit = qs('[data-scroll-product-form]');
    const stockStatus = qs('[data-product-stock-status]');
    const getVariantStock = (variant) => {
      const tracked = Boolean(variant.inventory_management);
      const rawQuantity = variant.inventory_quantity;
      const quantity = Number(rawQuantity);
      const hasQuantity = rawQuantity !== null && rawQuantity !== undefined && rawQuantity !== '' && Number.isFinite(quantity);
      const soldOut = variant.available === false || (variant.available === undefined && tracked && hasQuantity && quantity <= 0);
      return { tracked, hasQuantity, quantity: hasQuantity ? quantity : 0, soldOut };
    };
    const syncStockStatus = (variant) => {
      if (!stockStatus || !variant) return;
      const { hasQuantity, quantity, soldOut } = getVariantStock(variant);
      stockStatus.hidden = !soldOut && !(hasQuantity && quantity > 0 && quantity <= 4);
      stockStatus.classList.toggle('stock-badge', soldOut);
      stockStatus.classList.toggle('stock-badge--out', soldOut);
      stockStatus.classList.toggle('stock-count', !soldOut && hasQuantity && quantity > 0 && quantity <= 4);
      stockStatus.textContent = soldOut ? 'Out of stock' : hasQuantity && quantity > 0 && quantity <= 4 ? `Only ${quantity} left` : '';
    };
    form.addEventListener('change', () => {
      const selected = [1, 2, 3].map((position) => qs(`[data-option-position="${position}"]:checked`, form)?.value).filter(Boolean);
      const match = variants.find((variant) => selected.every((value, index) => variant.options[index] === value));
      if (!match) return;
      idInput.value = match.id;
      const { soldOut } = getVariantStock(match);
      syncStockStatus(match);
      if (submit) {
        submit.disabled = soldOut;
        submit.textContent = soldOut ? 'Sold out' : 'Add to cart';
      }
      if (mobileSubmit) {
        mobileSubmit.disabled = soldOut;
        mobileSubmit.classList.toggle('is-disabled', soldOut);
        mobileSubmit.textContent = soldOut ? 'Sold out' : 'Add to cart';
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('button[type="submit"]', form);
      const defaultLabel = button?.textContent.trim() || 'Add to cart';
      let productWasAdded = false;
      let status = qs('[data-product-form-status]', form);
      if (!status) {
        status = document.createElement('p');
        status.className = 'product-form__status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.dataset.productFormStatus = '';
        form.append(status);
      }
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding to cart...';
      }
      status.textContent = 'Adding this item to your cart.';
      try {
        const formData = new FormData(form);
        formData.set('sections', 'cart-drawer');
        formData.set('sections_url', window.location.pathname);
        const response = await fetch(cartAjaxUrl(window.themeRoutes?.cart_add_url || form.action), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => ({}));
          throw new Error(errorPayload.description || errorPayload.message || 'This size could not be added.');
        }
        const addPayload = await response.json().catch(() => ({}));
        productWasAdded = true;
        await updateCartCount();
        const updatedFromAdd = replaceCartDrawer(addPayload?.sections?.['cart-drawer']);
        const drawerUpdated = updatedFromAdd || await refreshCartDrawer();
        if (!drawerUpdated) {
          window.location.assign(window.themeRoutes?.cart_url || '/cart');
          return;
        }
        status.textContent = 'Added to your cart.';
        setOpen(qs('#CartDrawer'), true);
      } catch (error) {
        if (productWasAdded) {
          status.textContent = 'Added to your cart.';
          setOpen(qs('#CartDrawer'), true);
        } else {
          status.textContent = error.message || 'This item could not be added.';
        }
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = defaultLabel;
        }
      }
    });
  });

  function setupPageLoader() {
    const loader = qs('[data-page-loader]');
    if (!loader) return;
    const hide = () => loader.classList.add('is-hidden');
    window.addEventListener('load', hide, { once: true });
    window.setTimeout(hide, 2200);
  }

  setupPageLoader();
})();
